import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  TextField,
  MenuItem,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const formatBytes = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
};

const labelProcessType = (pt, t) => {
  const map = {
    anonymization: t("tenantUsage.anon.pt.anonymization", "Anonymizácia"),
    pseudonymization: t("tenantUsage.anon.pt.pseudonymization", "Pseudonymizácia"),
    depseudonymization: t("tenantUsage.anon.pt.depseudonymization", "De-pseudonymizácia"),
  };
  return map[pt] || pt || "—";
};

export default function TenantUsageSection({
  disabled,
  onLoadStats,
  onLoadLogs,
  onLoadAnonymizationStats, // ✅ new
}) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);

  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState(null);

  const [anonStats, setAnonStats] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l, a] = await Promise.all([
        onLoadStats?.(days),
        onLoadLogs?.({ limit: 50 }),
        onLoadAnonymizationStats ? onLoadAnonymizationStats(days) : Promise.resolve(null),
      ]);

      setStats(s || null);
      setLogs(l || null);
      setAnonStats(a || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const anonTotals = anonStats?.totals || null;

  const byStatus = useMemo(() => {
    const m = new Map();
    (anonStats?.by_status || []).forEach((x) => m.set(x.status, x.count));
    return {
      succeeded: m.get("succeeded") || 0,
      failed: m.get("failed") || 0,
    };
  }, [anonStats]);

  const byProcessType = Array.isArray(anonStats?.by_process_type) ? anonStats.by_process_type : [];

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t("tenantUsage.days", "Dni")}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          disabled={disabled}
          sx={{ width: 160 }}
        >
          {[7, 14, 30, 90, 180, 365].map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="outlined" onClick={refresh} disabled={disabled}>
          {t("tenantUsage.actions.refresh", "Obnoviť")}
        </Button>
      </Stack>

      {/* Existing: tenant stats */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {t("tenantUsage.statsTitle", "Štatistiky")}
      </Typography>

      {stats ? (
        <Box sx={{ mt: 1, mb: 3 }}>
          <Typography variant="body2">
            {t("tenantUsage.activeUsers", "Aktívni používatelia")}: <b>{stats.active_users}</b>
          </Typography>
          <Typography variant="body2">
            {t("tenantUsage.windowDays", "Okno (dni)")}: <b>{stats.window_days}</b>
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {t("tenantUsage.noStats", "Žiadne štatistiky.")}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* NEW: anonymization runs stats */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {t("tenantUsage.anon.title", "Anonymizácia")}
      </Typography>

      {!anonStats ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {t("tenantUsage.anon.noData", "Žiadne dáta o anonymizácii.")}
        </Typography>
      ) : (
        <Box sx={{ mt: 1, mb: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip
              size="small"
              label={`${t("tenantUsage.anon.runsTotal", "Počet behov")}: ${anonTotals?.runs_total ?? 0}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${t("tenantUsage.anon.succeeded", "OK")}: ${byStatus.succeeded}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${t("tenantUsage.anon.failed", "Chyby")}: ${byStatus.failed}`}
            />
          </Stack>

          <Typography variant="body2">
            {t("tenantUsage.anon.pagesTotal", "Strany spolu")}: <b>{anonTotals?.pages_total ?? 0}</b>
          </Typography>
          <Typography variant="body2">
            {t("tenantUsage.anon.itemsTotal", "Zásahy spolu")}: <b>{anonTotals?.anonymized_total ?? 0}</b>
          </Typography>
          <Typography variant="body2">
            {t("tenantUsage.anon.inputBytes", "Vstup dát")}: <b>{formatBytes(anonTotals?.input_bytes_total ?? 0)}</b>
          </Typography>
          <Typography variant="body2">
            {t("tenantUsage.anon.outputBytes", "Výstup dát")}: <b>{formatBytes(anonTotals?.output_bytes_total ?? 0)}</b>
          </Typography>

          {byProcessType.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {t("tenantUsage.anon.breakdown", "Rozpis podľa typu")}
              </Typography>

              <Stack spacing={1}>
                {byProcessType.map((row) => (
                  <Box
                    key={row.process_type}
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      alignItems: "center",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Chip size="small" label={labelProcessType(row.process_type, t)} />
                    <Typography variant="body2">
                      {t("tenantUsage.anon.count", "Behy")}: <b>{row.count ?? 0}</b>
                    </Typography>
                    <Typography variant="body2">
                      {t("tenantUsage.anon.pages", "Strany")}: <b>{row.pages ?? 0}</b>
                    </Typography>
                    <Typography variant="body2">
                      {t("tenantUsage.anon.items", "Zásahy")}: <b>{row.anonymized ?? 0}</b>
                    </Typography>
                    <Typography variant="body2">
                      {t("tenantUsage.anon.in", "Vstup")}: <b>{formatBytes(row.input_bytes ?? 0)}</b>
                    </Typography>
                    <Typography variant="body2">
                      {t("tenantUsage.anon.out", "Výstup")}: <b>{formatBytes(row.output_bytes ?? 0)}</b>
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Existing: logs */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {t("tenantUsage.logsTitle", "Logy")}
      </Typography>

      {logs?.results?.length ? (
        <Box sx={{ mt: 1 }}>
          {logs.results.slice(0, 12).map((r) => (
            <Typography key={r.id} variant="body2" sx={{ fontFamily: "monospace" }}>
              {new Date(r.occurred_at).toLocaleString()} — {r.event_type} — {r.status}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("tenantUsage.noLogs", "Žiadne logy.")}
        </Typography>
      )}
    </Box>
  );
}